// Direct port of the legacy index.html video resilience logic
// (loadVideo / attachVideoHandlers / watchdog / retry / recreate-element /
// fallback-to-images). This is the code that actually keeps video playing
// through a dropped connection on this hardware — translated to TS, not
// redesigned. Operates imperatively on a <video> element via refs, same as
// the original vanilla script, because the retry/backoff/decoder-reset
// state machine doesn't map cleanly onto React re-renders.

import { diag, resourceStats } from "@/lib/diag";

const WATCHDOG_INTERVAL_MS = 3000;
const STALL_THRESHOLD = 2; // 2 ticks = 6s frozen -> retry
const MAX_VIDEO_RETRIES = 8;
// Soft resumes (nudge playback, keep the buffer) before any full reload is
// considered. Only a hard MediaError skips straight past these.
const SOFT_RETRIES = 4;
// How long to wait for the in-memory copy before streaming so the screen is
// not black. The cached copy still takes over as soon as it is ready.
const STREAM_WHILE_CACHING_MS = 15000;
// play() attempts before giving up on a single load.
const PLAY_ATTEMPTS = 5;
// Watchdog ticks between status heartbeats (3s each -> every 2 minutes).
const HEARTBEAT_TICKS = 40;
// When to snapshot the video element after a source is applied.
const PROBE_TIMES_MS = [3000, 10000, 30000];
// How long a cached copy gets to produce metadata before we fall back to
// streaming. Healthy devices reach readyState>0 in well under a second.
const BLOB_METADATA_GRACE_MS = 12000;
// How long to give an in-memory copy to prove itself off-screen. Generous on
// purpose: nothing is visible to the viewer while this runs, and the TVs often
// need well over ten seconds to open a blob they will then play perfectly.
const BLOB_PREFLIGHT_MS = 45000;

// ── In-memory video cache ────────────────────────────────────────────────────
// Pointing <video> at a URL let the TVs' browser re-download the whole file on
// every loop: with no usable HTTP cache, four looping screens streamed their
// videos continuously — measured at ~19 Mbps, the four bitrates summed — which
// is what drove ~150 GB/day of Supabase egress. So each video is fetched ONCE
// into a Blob and played from an object URL: looping from memory costs no
// network at all, and playback also survives a dropped connection.
//
// Keyed by the configured URL, not the element, so retries, decoder resets
// and element recreation all reuse the same bytes instead of refetching.
const blobUrls = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();
// URLs whose blob a device refused to decode; those fall back to streaming
// directly rather than retrying a blob that will never play.
const blobRejected = new Set<string>();

// Name shared with the service worker; changing one means changing both.
const VIDEO_CACHE = "signage-video-v1";
// Videos known to be stored locally. Without this the dashboard cannot tell a
// URL served from disk by the worker from one being streamed off the network —
// they look identical on the element, and that is the whole question.
const storedLocally = new Set<string>();

/** Where the picture is actually coming from, for the dashboard. */
function videoSourceMode(url: string, isBlob: boolean): "memory" | "local" | "stream" {
  if (isBlob) return "memory";
  return storedLocally.has(url) && navigator.serviceWorker?.controller ? "local" : "stream";
}
// A device that refused an in-memory copy will refuse the next one too. Kept
// across reloads so a TV does not re-download a file every restart only to
// fail on it again — that doubled first-load traffic and filled the error feed.
const BLOB_UNSUPPORTED_KEY = "signage.blobUnsupported";

function blobPlaybackKnownBroken() {
  try {
    return localStorage.getItem(BLOB_UNSUPPORTED_KEY) === "1";
  } catch {
    return false;
  }
}

function rememberBlobPlaybackBroken() {
  try {
    localStorage.setItem(BLOB_UNSUPPORTED_KEY, "1");
  } catch {
    // Storage blocked: we simply retest next reload.
  }
}

/** Is this video already stored where the service worker can serve it? */
async function isInServiceWorkerCache(url: string): Promise<boolean> {
  try {
    if (!("caches" in window)) return false;
    const cache = await caches.open(VIDEO_CACHE);
    const hit = await cache.match(url, { ignoreVary: true });
    if (!hit) return false;
    const blob = await hit.blob();
    return blob.size > 0;
  } catch {
    return false;
  }
}

/**
 * Hand the downloaded file to the service worker's cache.
 *
 * This is the whole point on these TVs: they cannot play an in-memory copy,
 * but they play an ordinary URL perfectly — and once the file is here, that
 * URL is answered locally, so looping costs no bandwidth and survives an
 * outage. Stored from the page, never fetched by the worker.
 */
async function storeForServiceWorker(url: string, blob: Blob) {
  try {
    if (!("caches" in window)) return;
    // Deliberately NOT conditional on navigator.serviceWorker.controller. On
    // the TVs the page finished downloading before the newly installed worker
    // claimed it, so that check skipped the store on every screen and they
    // kept streaming. The cache is independent of who controls this page: the
    // worker serves whatever is here, from this load or the next.
    const cache = await caches.open(VIDEO_CACHE);
    await cache.put(
      url,
      new Response(blob, {
        headers: { "Content-Type": blob.type || "video/mp4", "Content-Length": String(blob.size) },
      }),
    );
    storedLocally.add(url);
    diag("sw_cached", { mb: (blob.size / 1048576).toFixed(1), f: url.split("/").pop() });
  } catch (err) {
    diag("sw_cache_failed", { err: String((err as Error)?.message || err) });
  }
}

/**
 * Can this device actually open the in-memory copy?
 *
 * Answered on a one-pixel element rather than the screen's own, so a "no"
 * never reaches the viewer. Metadata plus a real duration is the test:
 * readyState alone can sit at 0 indefinitely on these TVs with no error event
 * to tell us anything is wrong.
 */
function blobPlayable(objectUrl: string): Promise<boolean> {
  return new Promise((resolve) => {
    const probe = document.createElement("video");
    probe.muted = true;
    probe.preload = "metadata";
    probe.playsInline = true;

    // In the page, not detached. These TV browsers appear to refuse media on
    // an element that is not in the document — a detached probe failed
    // instantly on every screen, while the on-screen element merely hung. One
    // invisible pixel, so the viewer sees nothing either way.
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = "position:fixed;left:0;top:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-1";
    document.body.appendChild(probe);

    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      probe.onloadedmetadata = null;
      probe.onerror = null;
      // Release the probe's own hold on the data; the blob itself lives on.
      probe.removeAttribute("src");
      try {
        probe.load();
      } catch {
        // Nothing to unload.
      }
      probe.remove();
      resolve(ok);
    };

    const timer = setTimeout(() => finish(false), BLOB_PREFLIGHT_MS);
    probe.onloadedmetadata = () => finish(Number.isFinite(probe.duration) && probe.duration > 0);
    probe.onerror = () => finish(false);
    probe.src = objectUrl;
    probe.load();
  });
}

// One verdict per video, shared by every caller.
//
// Three loads can start within the same second (a config event, a retry, a
// re-render), and each was testing the same copy separately: three identical
// failures reported per screen per reload, which buries anything real in the
// error feed.
const preflights = new Map<string, Promise<boolean>>();

function checkBlobOnce(url: string, objectUrl: string): Promise<boolean> {
  let pending = preflights.get(url);
  if (!pending) {
    // Report inside the shared promise, not at each caller. Sharing the test
    // alone still produced one error per waiter: one test, three reports.
    pending = blobPlayable(objectUrl)
      .then((ok) => {
        if (!ok) {
          diag("blob_stuck", { phase: "preflight", f: url.split("/").pop() });
          rememberBlobPlaybackBroken();
        }
        return ok;
      })
      .finally(() => preflights.delete(url));
    preflights.set(url, pending);
  }
  return pending;
}

/** Drop our in-memory copy of a video and let the browser reclaim the memory. */
function releaseBlob(url: string) {
  const objectUrl = blobUrls.get(url);
  if (!objectUrl) return;
  blobUrls.delete(url);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
}

/**
 * Resolve a configured video URL to what the element should actually load:
 * a cached object URL when possible, the original URL otherwise. Never
 * rejects — any fetch failure (offline, a cross-origin host without CORS)
 * degrades to the direct URL, which is exactly the old behaviour.
 */
function resolvePlayableSrc(url: string): Promise<string> {
  if (blobRejected.has(url) || typeof URL.createObjectURL !== "function") {
    return Promise.resolve(url);
  }
  const hit = blobUrls.get(url);
  if (hit) return Promise.resolve(hit);

  let pending = inflight.get(url);
  if (!pending) {
    const started = Date.now();
    // Already stored for the service worker? Then the plain URL is answered
    // locally: nothing to download, nothing to test, no bandwidth.
    pending = (async () => {
      // Already stored for the service worker? Then the plain URL is answered
      // locally: nothing to download, nothing to test, no bandwidth.
      if (await isInServiceWorkerCache(url)) {
        storedLocally.add(url);
        diag("sw_hit", { f: url.split("/").pop() });
        return url;
      }
      try {
        const blob = await downloadInChunks(url);
        console.log(`Video downloaded (${(blob.size / 1048576).toFixed(1)} MB):`, url);
        diag("cached", { mb: (blob.size / 1048576).toFixed(1), ms: Date.now() - started, f: url.split("/").pop() });
        // Store it for the service worker first: on the TVs that is what
        // actually plays. The in-memory copy is for browsers that support it.
        await storeForServiceWorker(url, blob);
        if (blobPlaybackKnownBroken()) return url;
        const objectUrl = URL.createObjectURL(blob);
        blobUrls.set(url, objectUrl);
        return objectUrl;
      } catch (err) {
        console.warn("Video cache fetch failed, streaming directly:", err);
        diag("cache_failed", { err: String(err && ((err as Error).message || err)), ms: Date.now() - started });
        return url;
      }
    })().finally(() => inflight.delete(url));
    inflight.set(url, pending);
  }
  return pending;
}

const CHUNK_BYTES = 1024 * 1024; // 1 MB
const CHUNK_ATTEMPTS = 4;
const CHUNK_CONCURRENCY = 4;

/** True while a video is being downloaded into memory for this element. */
export function isCaching(url: string) {
  return inflight.has(url);
}

/**
 * Download a video in 1 MB ranges instead of one long request.
 *
 * A single fetch of a 13-21 MB file kept failing on the TVs with "Failed to
 * fetch" — the venue's link is shared by four screens that were all streaming
 * at once, and a long-running request dies on it. Short ranged requests
 * survive that, and a failed piece is retried on its own instead of throwing
 * away the whole download.
 */
async function downloadInChunks(url: string): Promise<Blob> {
  let type = "video/mp4";

  /** One range, retried on its own. Returns null when the server ignored Range. */
  async function getRange(start: number, end: number): Promise<{ buf: ArrayBuffer; total: number } | null> {
    let lastErr: unknown;
    for (let attempt = 1; attempt <= CHUNK_ATTEMPTS; attempt++) {
      try {
        const res = await fetch(url, { headers: { Range: `bytes=${start}-${end}` }, cache: "no-store" });
        type = res.headers.get("Content-Type") || type;
        if (res.status === 206) {
          const total = Number((res.headers.get("Content-Range") || "").split("/")[1]);
          return { buf: await res.arrayBuffer(), total: Number.isFinite(total) && total > 0 ? total : 0 };
        }
        if (res.status === 200) return null; // whole file; caller handles it
        throw new Error(`HTTP ${res.status}`);
      } catch (err) {
        lastErr = err;
        if (attempt < CHUNK_ATTEMPTS) await new Promise((r) => setTimeout(r, 400 * attempt));
      }
    }
    throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
  }

  // First range doubles as the size probe.
  const first = await getRange(0, CHUNK_BYTES - 1);
  if (!first) {
    const res = await fetch(url, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return new Blob([await res.arrayBuffer()], { type: res.headers.get("Content-Type") || type });
  }

  const total = first.total || first.buf.byteLength;
  const parts: ArrayBuffer[] = [first.buf];
  const ranges: Array<[number, number]> = [];
  for (let start = first.buf.byteLength; start < total; start += CHUNK_BYTES) {
    ranges.push([start, Math.min(start + CHUNK_BYTES, total) - 1]);
  }
  parts.length = ranges.length + 1;

  // A few ranges in flight at once: one-at-a-time spends most of its time
  // waiting on round trips (measured 109s for 13 MB), while too many at once
  // would crowd out the other screens on the same link.
  let next = 0;
  const workers = Array.from({ length: Math.min(CHUNK_CONCURRENCY, ranges.length) }, async () => {
    for (let i = next++; i < ranges.length; i = next++) {
      const [s, e] = ranges[i];
      const got = await getRange(s, e);
      if (!got) throw new Error("Server stopped honouring range requests mid-download");
      parts[i + 1] = got.buf;
    }
  });
  await Promise.all(workers);

  const blob = new Blob(parts, { type });
  if (blob.size !== total) throw new Error(`Incomplete download: ${blob.size} of ${total} bytes`);
  return blob;
}

/** The cached object URL for a configured URL, if one exists yet. */
function cachedSrc(url: string): string {
  return blobUrls.get(url) ?? url;
}

/**
 * Free the memory held for videos no longer in the config. Call with the
 * current video list whenever config changes; the URL in use is always in it,
 * so a playing element never has its object URL revoked underneath it.
 */
export function pruneVideoCache(keep: string[]) {
  const wanted = new Set(keep);
  for (const [url, objectUrl] of blobUrls) {
    if (!wanted.has(url)) {
      URL.revokeObjectURL(objectUrl);
      blobUrls.delete(url);
    }
  }

  // Same for what is stored on disk for the service worker, or a year of
  // replaced adverts would fill the device.
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: "prune-videos", keep });
  } catch {
    // No worker on this device; the in-memory prune above is all there is.
  }
}

const MEDIA_ERROR_NAMES: Record<number, string> = {
  1: "ABORTED",
  2: "NETWORK",
  3: "DECODE",
  4: "SRC_NOT_SUPPORTED",
};

export interface VideoEngineHost {
  container: HTMLDivElement;
  loader: HTMLDivElement;
  getVideo: () => HTMLVideoElement;
  /** Called when the element must be destroyed and recreated (MediaError 4). */
  replaceVideo: (next: HTMLVideoElement) => void;
  /** Called after MAX_VIDEO_RETRIES exhausted with the network still up. */
  onExhausted: (url: string) => void;
}

export class VideoEngine {
  private host: VideoEngineHost;
  private currentUrl = "";
  private hasStarted = false;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  private lastTime = -1;
  private stallTicks = 0;
  private retryCount = 0;
  private retryTimeout: ReturnType<typeof setTimeout> | null = null;
  private errorLock = false;
  private destroyed = false;

  constructor(host: VideoEngineHost) {
    this.host = host;
  }

  get activeUrl() {
    return this.currentUrl;
  }

  loadVideo(url: string) {
    this.currentUrl = url;
    this.retryCount = 0;
    this.hasStarted = false;
    this.errorLock = false;
    this.stopWatchdog();
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    const { container, loader } = this.host;
    const video = this.host.getVideo();

    container.classList.add("active");
    loader.classList.add("visible");
    video.muted = true;

    this.attachHandlers(video, url);

    // Same video already on the element (e.g. a config refetch that didn't
    // change the URL): just make sure it is playing. No reload, no refetch.
    //
    // It must actually HAVE a source: if a previous engine was torn down
    // between claiming the URL and applying it, this marker is set on an empty
    // element, and trusting it alone leaves the screen blank for ever.
    if (video.dataset.src === url && (video.src || video.currentSrc)) {
      this.startPlayback(video);
      return;
    }
    video.dataset.src = url;

    // Don't leave a screen black while a 13-21 MB file downloads over a slow
    // link — that can take minutes. If the copy isn't ready shortly, start
    // streaming so there is something on screen, and swap to the in-memory
    // copy the moment it lands (resuming at the same position).
    let streaming = false;
    const streamFallback = setTimeout(() => {
      if (this.destroyed || this.currentUrl !== url || blobUrls.has(url)) return;
      streaming = true;
      const el = this.host.getVideo();
      el.src = url;
      el.load();
      this.startPlayback(el);
    }, STREAM_WHILE_CACHING_MS);

    resolvePlayableSrc(url).then(async (src) => {
      clearTimeout(streamFallback);
      // Config may have moved on to another video while this one downloaded.
      if (this.destroyed || this.currentUrl !== url) return;
      let el = this.host.getVideo();
      // Already streaming this exact URL and the cache gave up — leave it be.
      if (streaming && src === url) return;

      // Prove the in-memory copy works BEFORE putting it on the live element.
      //
      // These TVs accept a blob and then sit at readyState 0 for ever with no
      // error, so the old order — swap first, detect the hang 12s later — put
      // a dead frame on a screen in a shop window and gave the copy no real
      // chance. Testing off-screen costs the viewer nothing either way.
      if (src !== url) {
        // Keep something on the screen while we test. When the download beats
        // the stream-fallback timer, nothing has been put on the element yet,
        // and the preflight can take the better part of a minute — that would
        // be a black screen in a shop window for the whole of it.
        if (!streaming) {
          streaming = true;
          el.src = url;
          el.load();
          this.startPlayback(el);
        }
        const ok = await checkBlobOnce(url, src);
        if (this.destroyed || this.currentUrl !== url) return;
        el = this.host.getVideo();
        if (!ok) {
          blobRejected.add(url);
          releaseBlob(url);
          if (!streaming) {
            el.src = url;
            el.load();
            this.startPlayback(el);
            this.probeAfterLoad(url);
          }
          return;
        }
        diag("blob_ready", { f: url.split("/").pop() });
      }

      const resumeAt = streaming && el.currentTime > 0.5 ? el.currentTime : 0;
      el.src = src;
      el.load();
      if (resumeAt > 0) {
        el.addEventListener(
          "loadedmetadata",
          () => {
            if (this.currentUrl !== url) return;
            try {
              el.currentTime = Math.min(resumeAt, Math.max(0, el.duration - 0.5));
            } catch {
              // Seeking unsupported — start from the top.
            }
          },
          { once: true },
        );
      }
      this.startPlayback(el);
      this.probeAfterLoad(url);
      if (src !== url) this.guardBlobPlayback(url, el);
    });
  }

  /**
   * Rescue a screen when the in-memory copy never loads.
   *
   * Screen 6 (a 1 GB TV with the largest video) sat at readyState 0 with
   * networkState "loading", no duration and NO error — for ever. These TVs
   * silently fail to open a blob that big, so nothing downstream could detect
   * it. If no metadata has arrived within the grace period, give up on the
   * cached copy for this URL and stream it instead, which is known to work.
   */
  private guardBlobPlayback(url: string, video: HTMLVideoElement) {
    setTimeout(() => {
      if (this.destroyed || this.currentUrl !== url) return;
      const el = this.host.getVideo();
      if (!el || !el.currentSrc.startsWith("blob:")) return;
      if (el.readyState > 0) return; // metadata arrived: the blob is fine

      diag("blob_stuck", { rs: el.readyState, ns: el.networkState, mb: (blobUrls.get(url) ? 1 : 0) });
      console.warn("In-memory copy never loaded; streaming instead:", url);

      blobRejected.add(url);
      const objectUrl = blobUrls.get(url);
      if (objectUrl) {
        blobUrls.delete(url);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
      }
      el.src = url;
      el.load();
      this.startPlayback(el);
      this.probeAfterLoad(url);
    }, BLOB_METADATA_GRACE_MS);
  }

  /** Report element state at fixed points after a load, even if no event fires. */
  private probeAfterLoad(url: string) {
    for (const at of PROBE_TIMES_MS) {
      setTimeout(() => {
        if (this.destroyed || this.currentUrl !== url) return;
        const v = this.host.getVideo();
        if (!v) return;
        diag("probe", {
          at: at / 1000,
          rs: v.readyState,
          ns: v.networkState,
          paused: v.paused,
          ct: v.currentTime.toFixed(1),
          dur: Number.isFinite(v.duration) ? v.duration.toFixed(1) : "nan",
          blob: v.currentSrc.startsWith("blob:"),
          err: v.error ? v.error.code : 0,
          hidden: document.hidden,
        });
      }, at);
    }
  }

  private startPlayback(video: HTMLVideoElement, attempt = 1) {
    const { loader } = this.host;
    video.muted = true; // required for unattended autoplay
    const playPromise = video.play();
    if (playPromise === undefined) return;
    playPromise
      .then(() => loader.classList.remove("visible"))
      .catch((err) => {
        // A play() right after load() is often interrupted; retry a few times
        // with a short gap instead of leaving the screen frozen.
        console.warn(`play() attempt ${attempt} failed:`, err && err.name);
        if (attempt >= PLAY_ATTEMPTS) {
          diag("play_failed", { err: String(err && (err.name || err)), hidden: document.hidden });
          return;
        }
        setTimeout(() => {
          if (!video.paused) return;
          this.startPlayback(video, attempt + 1);
        }, 800 * attempt);
      });
  }

  destroy() {
    this.destroyed = true;
    this.stopWatchdog();
    if (this.retryTimeout) clearTimeout(this.retryTimeout);
  }

  attachHandlers(video: HTMLVideoElement, url: string) {
    const { loader } = this.host;

    video.oncanplay = () => loader.classList.remove("visible");

    // Loop by hand rather than with the native loop attribute. On these TVs a
    // blob-backed video reached the end, wrapped to 0 and stuck there for good
    // — screen 2 reported pauses at ct=23.9 then ct=0.0 over and over, with no
    // error. Seeking explicitly and calling play() again recovers reliably.
    video.loop = false;
    video.onended = () => {
      if (this.currentUrl !== url) return;
      diag("looped", {
        blob: video.currentSrc.startsWith("blob:"),
        src: videoSourceMode(url, video.currentSrc.startsWith("blob:")),
        hidden: document.hidden,
      });
      try {
        video.currentTime = 0;
      } catch {
        // Seek refused — reloading the same source is the fallback.
      }
      this.startPlayback(video);
    };

    video.onplaying = () => {
      loader.classList.remove("visible");
      this.stallTicks = 0;
      this.errorLock = false;
      if (!this.hasStarted) {
        this.hasStarted = true;
        diag("playing", { blob: video.currentSrc.startsWith("blob:"), retry: this.retryCount });
        this.startWatchdog(url);
      }
    };

    // 'stalled' only means the network stopped delivering data — it fires
    // routinely while streaming a large file over a slow link, even though
    // playback continues fine from the buffer. Reloading here re-downloads
    // from byte 0 and throws the buffer away, which causes the next stall:
    // that feedback loop is what exhausted the retry budget on the TVs.
    // Genuine freezes are caught by the watchdog, which checks that
    // currentTime is actually advancing.
    video.onstalled = () => {
      if (this.hasStarted) console.warn("video stalled (buffering) - letting watchdog judge");
    };

    video.onwaiting = () => console.log("video waiting for data");

    video.onerror = () => {
      // Log the numeric code — "MediaError" alone says nothing. 1=ABORTED,
      // 2=NETWORK, 3=DECODE, 4=SRC_NOT_SUPPORTED.
      const e = video.error;
      diag("media_error", { code: e?.code, blob: video.currentSrc.startsWith("blob:"), msg: e?.message });
      console.error(
        `video error code=${e?.code ?? "?"} (${MEDIA_ERROR_NAMES[e?.code ?? 0] ?? "unknown"})`,
        e?.message || "",
        url,
      );
      this.scheduleRetry(url);
    };

    // Always attempt to resume, even when the page reports itself hidden.
    // TV browsers pause muted video-only playback to save power, and some
    // report document.hidden while fullscreen on a kiosk — with the old
    // `!document.hidden` guard here and in the watchdog, a single pause froze
    // the screen permanently with no error and no way back.
    video.onpause = () => {
      if (this.currentUrl !== url || video.ended) return;
      diag("paused", { hidden: document.hidden, ct: video.currentTime.toFixed(1), blob: video.currentSrc.startsWith("blob:") });
      setTimeout(() => {
        if (video.paused && this.currentUrl === url) this.startPlayback(video);
      }, 1200);
    };
  }

  private startWatchdog(url: string) {
    this.stopWatchdog();
    this.lastTime = -1;
    this.stallTicks = 0;
    // Heartbeat so a screen's real state is visible remotely — nobody can open
    // devtools on a TV, and a frozen screen otherwise reports nothing at all.
    let ticks = 0;

    this.watchdogTimer = setInterval(() => {
      if (this.destroyed || this.currentUrl !== url) {
        this.stopWatchdog();
        return;
      }

      const video = this.host.getVideo();
      if (!video) return;

      if (++ticks % HEARTBEAT_TICKS === 0) {
        // Dropped frames say whether the TV is keeping up with the video it
        // has been given; on a 1 GB device that is not a given.
        let dropped: number | undefined;
        try {
          const q = video.getVideoPlaybackQuality?.();
          if (q) dropped = q.droppedVideoFrames;
        } catch {
          // Not supported on this browser.
        }
        diag("state", {
          blob: video.currentSrc.startsWith("blob:"),
          src: videoSourceMode(this.currentUrl, video.currentSrc.startsWith("blob:")),
          paused: video.paused,
          hidden: document.hidden,
          ct: video.currentTime.toFixed(1),
          rs: video.readyState,
          stalls: this.stallTicks,
          drop: dropped,
          ...resourceStats(),
        });
      }

      if (video.paused) {
        // Same reasoning as onpause: never let "hidden" stop a signage screen.
        this.startPlayback(video);
        return;
      }

      if (!video.paused && !video.ended) {
        if (video.currentTime === this.lastTime) {
          this.stallTicks++;
          console.warn(`Watchdog: frozen at ${this.lastTime}s (tick ${this.stallTicks})`);
          if (this.stallTicks >= STALL_THRESHOLD) this.scheduleRetry(url);
        } else {
          this.stallTicks = 0;
        }
        this.lastTime = video.currentTime;
      }
    }, WATCHDOG_INTERVAL_MS);
  }

  private stopWatchdog() {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  private scheduleRetry(url: string) {
    if (this.retryTimeout || this.currentUrl !== url || this.errorLock) return;

    // Don't burn retries while offline — reconnect handler restarts playback.
    if (!navigator.onLine) {
      console.log("Offline - retry paused, will recover on reconnection");
      return;
    }

    this.errorLock = true;
    this.retryCount++;

    if (this.retryCount > MAX_VIDEO_RETRIES) {
      console.error(`Video failed after ${MAX_VIDEO_RETRIES} retries`);
      diag("exhausted", { f: url.split("/").pop() });
      this.errorLock = false;
      this.host.onExhausted(url);
      return;
    }

    const delay = Math.min(1000 * this.retryCount, 8000);
    console.log(`Retry ${this.retryCount}/${MAX_VIDEO_RETRIES} in ${delay}ms`);
    this.retryTimeout = setTimeout(() => {
      this.errorLock = false;
      this.retryTimeout = null;
      this.retry(url);
    }, delay);
  }

  private retry(url: string) {
    if (this.currentUrl !== url) return;

    this.hasStarted = false;
    this.stopWatchdog();

    const video = this.host.getVideo();
    const { loader } = this.host;

    const hasHardError = !!video.error;
    const fromBlob = video.currentSrc.startsWith("blob:");

    // A reload re-downloads the file from byte 0. While streaming over a
    // congested link that is actively harmful: the restart adds traffic, which
    // causes the next stall, which triggers the next reload — the spiral that
    // left screens black after MAX_VIDEO_RETRIES. So reload only when there is
    // a genuine decode/network error, or when gentler recovery has already
    // failed several times. Playing from memory (or still downloading into it)
    // can never be fixed by a reload, so those never reload.
    const preferSoftResume = !hasHardError && (fromBlob || isCaching(url) || this.retryCount <= SOFT_RETRIES);

    if (preferSoftResume) {
      // Nudge playback and let the buffer refill; keep whatever is buffered.
      if (video.readyState >= 2) video.currentTime = Math.max(0, video.currentTime - 0.1);
      video
        .play()
        .then(() => {
          loader.classList.remove("visible");
          this.stallTicks = 0;
          this.hasStarted = true;
          this.startWatchdog(url);
        })
        .catch(() => {
          // Still not playing — keep waiting rather than restarting the
          // download; the watchdog will come back around.
          this.hasStarted = true;
          this.startWatchdog(url);
        });
      return;
    }

    this.fullReload(url, video, loader);
  }

  private fullReload(url: string, video: HTMLVideoElement, loader: HTMLDivElement) {
    if (this.currentUrl !== url) return;

    // Only a real reload justifies the spinner; a soft resume leaves the
    // last frame on screen rather than covering healthy video.
    loader.classList.add("visible");

    // A reload restarts the download at byte 0, so remember where playback
    // was and seek back once metadata lands — otherwise every recovery
    // restarts the menu loop from the beginning.
    const resumeAt = video.currentTime > 1 ? video.currentTime : 0;

    // A decode/unsupported error while playing from an in-memory blob can't be
    // a network hiccup — the bytes are all local. This device won't play the
    // blob, so stream this URL directly from now on rather than retrying it.
    const code = video.error?.code;
    if ((code === 3 || code === 4) && video.currentSrc.startsWith("blob:")) {
      console.warn("Device rejected in-memory video; falling back to direct URL:", url);
      diag("blob_rejected", { code, msg: video.error?.message });
      blobRejected.add(url);
      const objectUrl = blobUrls.get(url);
      if (objectUrl) {
        blobUrls.delete(url);
        // Revoke after the element has dropped it, not while it's attached.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
      }
    }

    let target = video;
    if (video.error && video.error.code === 4) {
      // MediaError 4 (SRC_NOT_SUPPORTED) after prolonged instability means the
      // decoder is stuck; only a fresh element resets it.
      console.warn("MediaError 4 - recreating video element to reset decoder");
      target = this.recreateElement(video);
      this.attachHandlers(target, url);
    } else {
      video.dataset.src = "";
      video.removeAttribute("src");
      video.load();
    }

    setTimeout(() => {
      if (this.currentUrl !== url) return;
      target.dataset.src = url;
      // Reuse the in-memory copy: a recovery must not re-download the video.
      target.src = cachedSrc(url);
      target.load();
      if (resumeAt > 0) {
        target.addEventListener(
          "loadedmetadata",
          () => {
            if (this.currentUrl !== url) return;
            try {
              target.currentTime = Math.min(resumeAt, Math.max(0, target.duration - 0.5));
            } catch {
              // Seeking unsupported/out of range — start from the top.
            }
          },
          { once: true },
        );
      }
      target
        .play()
        .then(() => {
          loader.classList.remove("visible");
          this.stallTicks = 0;
        })
        .catch(() => this.scheduleRetry(url));
    }, 500);
  }

  private recreateElement(old: HTMLVideoElement): HTMLVideoElement {
    old.onerror = null;
    old.onstalled = null;
    old.onwaiting = null;
    old.onplaying = null;
    old.oncanplay = null;
    old.onpause = null;
    old.pause();
    old.removeAttribute("src");
    old.load();

    const next = document.createElement("video");
    next.id = "video-player";
    next.autoplay = true;
    next.loop = false; // looping is driven by the onended handler; see attachHandlers
    next.muted = true;
    next.setAttribute("playsinline", "");
    next.setAttribute("preload", "auto");

    old.replaceWith(next);
    this.host.replaceVideo(next);
    return next;
  }

  /** Called from the reconnect handler to force a resume/retry check. */
  recoverFromOffline() {
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    this.errorLock = false;
    this.retryCount = 0;

    const video = this.host.getVideo();
    if (this.currentUrl && video && (video.error || video.paused || this.stallTicks > 0)) {
      this.retry(this.currentUrl);
    }
  }
}
